import { useToken } from '@chakra-ui/react';
import { useState, useEffect, RefObject, useRef, useMemo } from "react";

import { BCAVisibleSignatureOptions } from "bca-client-signature/lib/BCAQSCDTypes";
import { $count, $ok, $url, $UUID } from "foundation-ts/commons";
import { $uuid } from "foundation-ts/crypto";
import { $decodeBase64 } from "foundation-ts/data";
import { Nullable, uint8, UUID } from "foundation-ts/types";
import { DocumentSignaturePosition } from "g1-commons/lib/doxecureTypes";
import { DocumentDto, SignatureDto } from "g1-commons/lib/doxecureClientTypes";

import viewerHTML from "@/internal/viewer.html?raw";
import viewerCSS from "@/internal/viewer.css?raw";
import errorJS from "@/internal/errors.js?raw";
import callbackJS from "@/internal/callbacks.js?raw";
import viewerJS from "@/internal/viewer.js?raw";

import { config } from '@/config/config';
import { getDocumentSource } from "@hooks/useDocumentSource";
import { $g1Color } from "@/theme/G1Style";
import { bytesToDownloadURL, optlog } from "@utils/functions";
import { AvailableSignersMessage, 
         DocumentNamesMessage,
         DocumentURLDataRespMessage,
         DocumentURLDataReqMessage,
         InitViewerMessage,
         InitViewerStyles,
         LoadingFailedMessage, 
         LoadingFailedType,
         PlacedSignersMessage,
         ShowSignerPositionMessage,
         SignerInfos,
         isDocumentURLDataReqMessage,
         isLoadingFailedMessage,
         isPrintDocMessage,
         isRefuseDocMessage,
         isRemoveSignerPositionMessage,
         isSaveSignerPositionMessage,
         isSignDocMessage,
         RemainingSigners } from "@utils/viewPDFTypes";

export interface ViewerAction {
    html:string|JSX.Element ;
    action:(index:Nullable<number>) => void|Promise<void> ;
}

interface ViewerProps {
    viewerRef?: RefObject<HTMLIFrameElement>,
    documents: DocumentDto[];
    lastPageSignRows: uint8;
    immutable: boolean;
    signatures?: SignatureDto[];
    forceMobile?: boolean;
    placingSignersView?: boolean;
    signingView?: boolean;
    autoExternalSavePosition?: boolean;
    showRefuseButton?: boolean;
    signerPositions?: DocumentSignaturePosition[];
    remainingSigners?: RemainingSigners[];
    signingVisibleOptions?: Nullable<BCAVisibleSignatureOptions>;
    token?: Nullable<string> ;
    onLoadingFailed?: (type: LoadingFailedType) => void;
    onRemoveSignerPosition?: (docId: UUID, signatureId: UUID) => void;
    onSaveSignerPosition?: (docId: UUID, docPosition: DocumentSignaturePosition) => void;
    onSignDocuments?: () => void;
    onRefuseDocuments?: () => void;
}

const PADDING_TO_REMOVE = 10;

export const Viewer = (props: ViewerProps) => {
    const { viewerRef, documents, signatures, lastPageSignRows, immutable, placingSignersView, signingView, autoExternalSavePosition, showRefuseButton, signerPositions, remainingSigners, signingVisibleOptions, token, onSaveSignerPosition, onRemoveSignerPosition, onSignDocuments, onRefuseDocuments, onLoadingFailed } = props ;
    const [remainingHeight, setRemainingHeight] = useState<number>(0);
    const docPositions = useRef<DocumentSignaturePosition[]>([]);
    const savedLastPageSignRows = useRef(0);
    const savedPositionsOnLastPage = useRef<DocumentSignaturePosition[]>([]);
    const savedAvailableSignersCount = useRef(0);
    const savedDocumentId = useRef<UUID>();
    const ref = useRef<HTMLDivElement>(null);
    const barColors = useToken('colors', [$g1Color("viewer.toolbar.bg"), $g1Color("viewer.sidebar.bg")]);
    const textbarColors = useToken('colors', [$g1Color("viewer.write"), $g1Color("viewer.sidebar.write")]);
    const buttonColors = useToken('colors', [$g1Color("global.buttons.optional.bg"), $g1Color("form.buttons.bg"), $g1Color("form.buttons.important.bg")]);
    const hoverButtonColors = useToken('colors', [$g1Color("global.buttons.optional.hover.bg"), $g1Color("form.buttons.hover.bg"), $g1Color("form.buttons.important.hover.bg")]);
    const viewerToken = useMemo(() => $uuid(), []);
    const fullHTML = useMemo(() => {
        let html = viewerHTML;
        if ($ok(viewerCSS)) {
            html = html.replace('</head>', `<style>${viewerCSS}</style><script>window.VIEWER_TOKEN='${viewerToken}'</script></head>`);
        }
        if ($ok(errorJS)) {
            html = html.replace('<script id="error"></script>', `<script>${errorJS}</script>`);
        }
        if ($ok(callbackJS)) {
            html = html.replace('<script id="callback"></script>', `<script>${callbackJS}</script>`);
        }
        if ($ok(viewerJS)) {
            html = html.replace('<script id="viewer"></script>', `<script>${viewerJS}</script>`);
        }
        return html;
    }, [viewerToken]);

    const viewerStyles: InitViewerStyles = {
        header: { background: barColors[0], text: { color: textbarColors[0] } },
        footer: { background: barColors[0], text: { color: textbarColors[0] } },
        sidebar: { background: barColors[1], text: { color:textbarColors[1] } },
        nextButton: { background: buttonColors[0], hover: { background: hoverButtonColors[0] } },
        previousButton: { background: buttonColors[0], hover: { background: hoverButtonColors[0] } },
        signButton: { background: buttonColors[1], hover: { background: hoverButtonColors[1] } },
        refuseButton: { background: buttonColors[2], hover: { background: hoverButtonColors[2] } }
    };

    const _handleSaveSignerPosition = (docId: UUID, docPosition: DocumentSignaturePosition) => {
        optlog("viewer save positon", docId, docPosition);
        const index = docPositions.current.findIndex(pos => pos.identifier === docPosition.identifier);
        if (index !== -1) { docPositions.current.splice(index, 1); }  // If it's an update position, then remove the old before insert the updated pos
        docPositions!.current.push(docPosition);
        onSaveSignerPosition?.(docId, docPosition);
    }

    const _handleRemoveSignerPosition = (docId: UUID, signatureId: UUID) => {
        optlog("viewer remove positon", docId, signatureId);
        const index = docPositions.current.findIndex(pos => pos.identifier === signatureId);
        if (index !== -1) { docPositions.current.splice(index, 1); }

        onRemoveSignerPosition?.(docId, signatureId);
    }

    const _sendAvailableSigners = (forceUpdateView?: Nullable<boolean>) => {
        const signers: SignerInfos[] = [];
        for (const signature of signatures!) {
            signers.push({ name: `${signature.signer.firstName} ${signature.signer.lastName}`, id: signature.signer.apid!, email: signature.signer.email });
        }
        const availableSignersMessage: AvailableSignersMessage = { type: 'AVAILABLE_SIGNERS', token: viewerToken,  payload: {signers, forceUpdateView } };
        viewerRef?.current?.contentWindow?.postMessage(availableSignersMessage, window.location.origin);
        savedAvailableSignersCount.current = $count(signers);
    }

    const _sendPlacedSigners = (docId: UUID, positions: DocumentSignaturePosition[], forceUpdateView?: Nullable<boolean>) => {
        const placedSignersMessage: PlacedSignersMessage = { type: "PLACED_SIGNERS", token: viewerToken, payload: { docId: docId!, signers: positions, forceUpdateView } };
        viewerRef?.current?.contentWindow?.postMessage(placedSignersMessage, window.location.origin);
    }

    const _handleLoadedIframe = async () => {
        const initViewerMessage: InitViewerMessage = { type: "INIT_VIEWER", token: viewerToken, payload: {
            appName: config.app.title,
            styles: viewerStyles,
            debug: config.debug,
            placingSignersView: placingSignersView,
            signingView: signingView,
            usingSelector: $count(documents) > 1,
            autoExternalSavePosition,
            showRefuseButton,
            callbackNames: {
                urldata: 'DOC_URL_DATA_REQ',
                signing: signingView ? 'SIGNING_REQ' : undefined,
                refuse: signingView ? 'REFUSING_REQ' : undefined,
                printing: 'PRINTING_REQ'
            },
            cguURL: $url(config.app.cguURL),
            privacyURL: $url(config.app.privacyURL!),
        }};
        viewerRef?.current?.contentWindow?.postMessage(initViewerMessage, window.location.origin);

        if ($count(documents) === 1) {
            const document = documents[0];
            optlog(`will render document "${document.fileName}"`);
            const docId =  document.apid ?? document.localUUID;
            if (!$ok(docId)) { return; }

            const docNamesMessage: DocumentNamesMessage = { type: 'ONE_DOCUMENT_NAME', token: viewerToken, docs: [{ docName: document.fileName, docId: docId! }] };
            viewerRef?.current?.contentWindow?.postMessage(docNamesMessage, window.location.origin);

            if (placingSignersView) {
                _sendAvailableSigners();
                optlog("document has been showed with nb visible => ", document.visibleSignatures);
                if ($count(document.visibleSignatures) > 0) { docPositions.current.push(...document!.visibleSignatures!); }
                optlog("lastPage = ", lastPageSignRows);
                optlog("pagecount = ", document.pagesCount);
                optlog("docPositions.current", docPositions.current);
                optlog("savedPositionsOnLastPage.current", savedPositionsOnLastPage);
                if ((lastPageSignRows === 0) && (docPositions.current.findIndex(pos => pos.page === document.pagesCount-1) !== -1)) { // Presence of signers at last page
                    savedPositionsOnLastPage.current.push(...docPositions.current.filter(pos => pos.page === document.pagesCount-1));
                    optlog("Save positions on last page", savedPositionsOnLastPage);
                }
                if ($count(docPositions.current) > 0) { _sendPlacedSigners(docId!, docPositions.current!); }
            }
        } else {
            const docNamesMessage: DocumentNamesMessage = { type: 'MULTIPLE_DOCUMENT_NAMES', token: viewerToken, docs: documents.filter(doc => $ok(doc.apid) || $ok(doc.localUUID)).map(doc => ({ docName: doc.fileName, docId: (doc.apid ?? doc.localUUID)! })) };
            viewerRef?.current?.contentWindow?.postMessage(docNamesMessage, window.location.origin);
        }
        if (!placingSignersView && ($count(signerPositions) > 0 || $count(remainingSigners) > 0)) {
            const showSignerPositionMessage: ShowSignerPositionMessage = { type: "SHOW_SIGNER_POSITION", token: viewerToken, positions: signerPositions!, remainingSigners, visibleOptions: signingVisibleOptions };
            viewerRef?.current?.contentWindow?.postMessage(showSignerPositionMessage, window.location.origin);
        }
    }

    const _handleViewerMessage = (event: MessageEvent) => {
        if (isLoadingFailedMessage(event.data, viewerToken)) {
            event.stopPropagation();
            onLoadingFailed?.((event.data as LoadingFailedMessage).error);
        } else if (isDocumentURLDataReqMessage(event.data, viewerToken)) {
            event.stopPropagation();
            const message = event.data as DocumentURLDataReqMessage;
            const document = documents.find(doc => doc.apid === message.docId || doc.localUUID === message.docId);
            if (!$ok(document)) { return; }

            optlog(`will render document "${document!.fileName}"`);
            getDocumentSource(document, immutable, lastPageSignRows, signatures, token).then(([filedata, e]) => {
                let error: Nullable<string> = undefined;
                if ($ok(e)) {
                    error = (e === 500) ? "Une erreur est survenue, le document ne peut pas être chargé" : "Ce document n'est plus disponible à la consultation (suite à l'expiration ou l'archivage de la session de signature)";
                } else if (!$ok(filedata?.pdfUrlData)) {
                    error = "Une erreur est survenue, le document ne peut pas être chargé";
                }
                const docId = document!.apid ?? document!.localUUID;
                const urlDataRespMessage: DocumentURLDataRespMessage = { type: "DOC_URL_DATA_RESP", token: viewerToken, payload: { docId: docId!, urlData: filedata?.pdfUrlData, error } };
                viewerRef?.current?.contentWindow?.postMessage(urlDataRespMessage, window.location.origin);
            });
        } else if (isSaveSignerPositionMessage(event.data, viewerToken)) {
            event.stopPropagation();
            _handleSaveSignerPosition(event.data.payload.docId, event.data.payload.docPosition);
        } else if (isRemoveSignerPositionMessage(event.data, viewerToken)) {
            event.stopPropagation();
            _handleRemoveSignerPosition(event.data.payload.docId, event.data.payload.signatureId);
        } else if (isPrintDocMessage(event.data, viewerToken)) {
            event.stopPropagation();
            const byteString = $decodeBase64(event.data.urlData.split(',')[1]);
            const mimeString = event.data.urlData.split(',')[0].split(':')[1].split(';')[0];
            const blobUrl = bytesToDownloadURL(byteString, mimeString);
            const printWindow = window.open(blobUrl, "_blank");
            if (!$ok(printWindow)) { return; }
            setTimeout(() => {
                printWindow?.focus();
                printWindow?.print();
            }, 500);
        } else if (isSignDocMessage(event.data, viewerToken)) {
            event.stopPropagation();
            onSignDocuments?.();
        } else if (isRefuseDocMessage(event.data, viewerToken)) {
            event.stopPropagation();
            onRefuseDocuments?.();
        }
    };

    const _measure = () => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const height = window.innerHeight - rect.top - PADDING_TO_REMOVE;
            setRemainingHeight(height);
        }
    };

    useEffect(() => {
        if ($ok(viewerCSS)) {
        }

        if (placingSignersView && $ok(viewerRef?.current)) {
            const document = documents[0];
            const docId = document.apid ?? document.localUUID;
            optlog("documentId => ", docId);

            // Allow to reinitialize working values when changing document view
            if (docId !== savedDocumentId.current) {
                docPositions.current.length = 0;
                savedPositionsOnLastPage.current.length = 0;
                savedLastPageSignRows.current = 0;
                savedAvailableSignersCount.current = 0;
                savedDocumentId.current = docId;
            }

            // Delete old savedPositions on last page which are not found in the last documentSignaturePosition list
            optlog("docPositions", docPositions.current);
            optlog("savedPositionsOnLastPage", savedPositionsOnLastPage.current);
            const savedPositionsOnLastPageToRemove: DocumentSignaturePosition[] = savedPositionsOnLastPage.current.filter(savedPos => !docPositions.current?.some(pos => pos.identifier === savedPos.identifier && pos.page === savedPos.page));
            optlog("savedPositionsOnLastPageToRemove", savedPositionsOnLastPageToRemove);
            const newPositionsOnLastPage = savedPositionsOnLastPage.current.filter(prevPosition => !savedPositionsOnLastPageToRemove.some(pos => pos.identifier === prevPosition.identifier));
            savedPositionsOnLastPage.current = newPositionsOnLastPage;
            optlog("positionOnLastPage", savedPositionsOnLastPage.current);

            // Force to re-render list of available signers because user add or remove some
            if ($count(signatures) !== savedAvailableSignersCount.current) { 
                _sendAvailableSigners(true);
            }
            if ($count(docPositions.current) > 0) {
                optlog("I position those drawn", $count(docPositions.current));
                _sendPlacedSigners(docId!, docPositions.current, true);
            }

            if (savedLastPageSignRows.current !== lastPageSignRows) { // Test to update only if needed
                savedLastPageSignRows.current = lastPageSignRows;
                if (lastPageSignRows === 0) { // Viewer with new last page
                    optlog("new page");
                    optlog("docPositions.current", docPositions.current);
                    const signaturesToPlace = signatures?.filter(sign => !docPositions.current?.some(pos => pos.identifier === sign.signer.apid!));
                    optlog("signaturesToPlace", signaturesToPlace);
                    getDocumentSource(document, immutable, lastPageSignRows, signaturesToPlace, token).then(([filedata, e]) => {
                        const error: Nullable<string> = $ok(e) || !$ok(filedata?.pdfUrlData) ? "Une erreur est survenue, le document ne peut pas être chargé" : null;
                        const urlDataRespMessage: DocumentURLDataRespMessage = { type: "DOC_URL_DATA_RESP", token: viewerToken, payload: { docId: docId!, urlData: filedata?.pdfUrlData, error } };
                        viewerRef?.current?.contentWindow?.postMessage(urlDataRespMessage, window.location.origin);
                        if ($ok(filedata?.visibleSignatures)) {
                            for (const position of filedata!.visibleSignatures!) {
                                docPositions.current.push(position);
                                savedPositionsOnLastPage.current.push(position);
                                onSaveSignerPosition?.(docId!, position);
                            }
                            _sendPlacedSigners(docId!, docPositions.current, true);
                        }
                    });
                } else { 
                    for (const position of savedPositionsOnLastPage.current) { // If user returns to place signature without new page, we must remove the signers present in last page
                        _handleRemoveSignerPosition(docId!, $UUID(position.identifier)!);
                    }
                    if ($count(savedPositionsOnLastPage.current) > 0) {
                        _sendPlacedSigners(docId!, docPositions.current, true);
                        savedPositionsOnLastPage.current = [];
                    }
                    getDocumentSource(document, immutable, lastPageSignRows, signatures, token).then(([filedata, e]) => {
                        const error: Nullable<string> = $ok(e) || !$ok(filedata?.pdfUrlData) ? "Une erreur est survenue, le document ne peut pas être chargé" : null;
                        const urlDataRespMessage: DocumentURLDataRespMessage = { type: "DOC_URL_DATA_RESP", token: viewerToken, payload: { docId: docId!, urlData: filedata?.pdfUrlData, error } };
                        viewerRef?.current?.contentWindow?.postMessage(urlDataRespMessage, window.location.origin);
                    });
                }
            }
        }

        _measure();
        window.addEventListener('resize', _measure);
        window.addEventListener("message", _handleViewerMessage);
        return () => { 
            window.removeEventListener('resize', _measure); 
            window.removeEventListener("message", _handleViewerMessage);
        };
    }, [documents]);

    return (
        <div ref={ref} style={{ height: remainingHeight, width: "100%" }}>
            <iframe ref={viewerRef} srcDoc={fullHTML} style={{ width: "100%", height: "100%", border: "none" }} onLoad={_handleLoadedIframe} />
        </div>
    );
};